import React, {useState} from 'react';
import {Modal, Text, FlexColumn} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';
import ConvertBaselineToPlaceholdersMutation from '../../mutations/ConvertBaselineToPlaceholdersMutation';
import Util from '../../forecast-app/shared/util/util';

export type Placeholder = {
	name: string;
	roleId: string;
	allocations: Allocation[];
};
export type Allocation = {
	startDate: string;
	endDate: string;
	monday: number;
	tuesday: number;
	wednesday: number;
	thursday: number;
	friday: number;
	saturday: number;
	sunday: number;
	description: string;
};

export type ConvertBaselineToPlaceholdersModalInput = {
	placeholders: Placeholder[];
};

type ConvertBaselineToPlaceholdersModalProps = {
	closeModal: () => void;
	baselineData?: ConvertBaselineToPlaceholdersModalInput;
	projectId?: string;
	setShowPlaceholderAlert: (show: boolean) => void;
	setPlaceholderCount: (count: number) => void;
	setPlaceholderAllocationCount: (count: number) => void;
};

const ConvertBaselineToPlaceholdersModal = ({
	closeModal,
	baselineData,
	projectId,
	setShowPlaceholderAlert,
	setPlaceholderCount,
	setPlaceholderAllocationCount,
}: ConvertBaselineToPlaceholdersModalProps): JSX.Element => {
	const [isProcessing, setIsProcessing] = useState(false);

	const handleConfirm = () => {
		if (!baselineData || !projectId || !baselineData.placeholders.length) {
			closeModal();
			return;
		}

		setIsProcessing(true);

		Util.CommitMutation(
			ConvertBaselineToPlaceholdersMutation,
			{
				projectId,
				placeholders: baselineData.placeholders,
			},
			response => {
				if (response.convertBaselineToPlaceholders.errors && response.convertBaselineToPlaceholders.errors.length > 0) {
					console.error('Failed to convert baseline to placeholders:', response.convertBaselineToPlaceholders.errors);
					closeModal();
					return;
				}
				setIsProcessing(false);
				setShowPlaceholderAlert(true);
				setPlaceholderCount(baselineData.placeholders.length);
				setPlaceholderAllocationCount(
					baselineData.placeholders.reduce((total, placeholder) => total + placeholder.allocations.length, 0)
				);
				closeModal();
			}
		);
	};

	const handleCancel = () => {
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="convert_baseline_to_placeholders.modal.title" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="m">
					<Text>
						<FormattedMessage id="convert_baseline_to_placeholders.modal.description" />
					</Text>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleConfirm}
					dataCy="convert-baseline-to-placeholders-confirm"
					disabled={isProcessing}
				>
					<FormattedMessage id="common.confirm" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton
					onPress={handleCancel}
					dataCy="convert-baseline-to-placeholders-cancel"
					disabled={isProcessing}
				>
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ConvertBaselineToPlaceholdersModal;
